<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  DAYS_PER_WEEK,
  dayKey,
  daysBetween,
  eventClasses,
  formatTimeRange,
  isAllDayEvent,
  useCalendarFormatters,
} from '@/composables/event-calendar'
import { ABSENCE_ICON, eventTypeEnum } from '@/stores/schedule.store'

const MAX_RESOURCE_CHIPS = 6
const SCROLL_OFFSET_PX = 8

const props = defineProps({
  rangeStart: {
    type: Object,
    required: true,
  },
  rangeEnd: {
    type: Object,
    required: true,
  },
  events: {
    type: Array,
    required: true,
  },
  now: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['event-click', 'load-more'])

const { t, locale } = useI18n()
const formatters = computed(() => useCalendarFormatters(locale.value))

const scrollRef = ref(null)
const sentinelRef = ref(null)
let intersectionObserver = null

const todayKey = computed(() => dayKey(props.now))

const spanOf = (event, day) => {
  const first = event.startAt.startOf('day')
  const days = event.endAt.subtract(1, 'ms').startOf('day').diff(first, 'day') + 1
  return days > 1 ? { day: day.diff(first, 'day') + 1, days } : null
}

const timeLabel = (start, end, day) => {
  const dayEnd = day.add(1, 'day')
  if (!start.isAfter(day) && !end.isBefore(dayEnd)) {
    return t('common.allDay')
  }
  return formatTimeRange(formatters.value, start.isBefore(day) ? day : start, end.isAfter(dayEnd) ? dayEnd : end)
}

const projectCards = (events, day) => {
  const groups = new Map()
  events.forEach(event => {
    const group = groups.get(event.project_id) ?? { root: null, allocations: [] }
    if (event.is_project_root) {
      group.root = event
    } else {
      group.allocations.push(event)
    }
    groups.set(event.project_id, group)
  })

  return [...groups.values()].flatMap(({ root, allocations }) => {
    const span = root ? spanOf(root, day) : null
    if (!allocations.length && span && span.day !== 1 && span.day !== span.days) {
      return []
    }
    const reference = root ?? allocations[0]
    const start = allocations.length ? allocations.reduce((min, e) => (e.startAt.isBefore(min) ? e.startAt : min), allocations[0].startAt) : root.startAt
    const end = allocations.length ? allocations.reduce((max, e) => (e.endAt.isAfter(max) ? e.endAt : max), allocations[0].endAt) : root.endAt
    const resources = [...new Map(allocations.map(e => [e.service_id, { id: e.service_id, label: e.title, color: e.resourceColor }])).values()]
    return [{
      key: `project-${reference.project_id}`,
      event: reference,
      classTarget: allocations.length
        ? { ...reference, is_project_root: false, confirmed: allocations.every(e => e.confirmed) }
        : root,
      title: reference.project?.name,
      time: allocations.length ? timeLabel(start, end, day) : t('common.allDay'),
      allDay: !allocations.length,
      start,
      span,
      resources: resources.slice(0, MAX_RESOURCE_CHIPS),
      moreResources: Math.max(0, resources.length - MAX_RESOURCE_CHIPS),
    }]
  })
}

const absenceCards = (events, day) => events.map(event => ({
  key: `absence-${event.id}`,
  event,
  classTarget: event,
  title: event.title || t('common.staff.absence'),
  time: timeLabel(event.startAt, event.endAt, day),
  allDay: isAllDayEvent(event),
  start: event.startAt,
  span: spanOf(event, day),
  resources: [],
  moreResources: 0,
}))

const cardsFor = (day) => {
  const end = day.add(1, 'day')
  const dayEvents = props.events.filter(event => event.startAt.isBefore(end) && event.endAt.isAfter(day))
  const absences = dayEvents.filter(event => event.type === eventTypeEnum.ABSENCE)
  const others = dayEvents.filter(event => event.type !== eventTypeEnum.ABSENCE)
  return [...projectCards(others, day), ...absenceCards(absences, day)]
    .sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.valueOf() - b.start.valueOf())
}

const nowIndex = (cards) => cards.filter(card => card.allDay || card.start.isBefore(props.now)).length

const months = computed(() => {
  const result = []
  daysBetween(props.rangeStart, props.rangeEnd).forEach(day => {
    const key = dayKey(day)
    const cards = cardsFor(day)
    const today = key === todayKey.value
    let month = result[result.length - 1]
    if (!month || !day.isSame(month.day, 'month')) {
      month = { key: day.format('YYYY-MM'), day, weeks: [] }
      result.push(month)
    }
    if (!cards.length && !today) {
      return
    }
    const weekStart = day.startOf('isoWeek')
    let week = month.weeks[month.weeks.length - 1]
    if (!week || !week.start.isSame(weekStart, 'day')) {
      week = {
        key: dayKey(weekStart),
        start: weekStart,
        label: `${formatters.value.dayMonth.format(weekStart.toDate())} – ${formatters.value.dayMonth.format(weekStart.add(DAYS_PER_WEEK - 1, 'day').toDate())}`,
        days: [],
      }
      month.weeks.push(week)
    }
    week.days.push({ day, key, today, past: day.isBefore(props.now, 'day'), cards, nowIndex: today ? nowIndex(cards) : -1 })
  })
  return result
})

const scrollToDate = (date, behavior = 'auto') => {
  const target = dayKey(date)
  const rows = [...(scrollRef.value?.querySelectorAll('[data-day]') ?? [])]
  const row = rows.find(element => element.dataset.day >= target) ?? rows[rows.length - 1]
  if (row) {
    scrollRef.value.scrollTo({ top: row.offsetTop - SCROLL_OFFSET_PX, behavior })
  }
}

defineExpose({ scrollToDate })

onMounted(() => {
  intersectionObserver = new IntersectionObserver(entries => {
    if (entries.some(entry => entry.isIntersecting) && !props.loading) {
      emit('load-more')
    }
  }, { root: scrollRef.value, rootMargin: '0px 0px 40% 0px' })
  intersectionObserver.observe(sentinelRef.value)
})

onBeforeUnmount(() => {
  intersectionObserver?.disconnect()
})
</script>
<template>
  <div ref="scrollRef" class="cal-agenda">
    <section
      v-for="month in months"
      :key="month.key"
      class="cal-agenda__month"
    >
      <header
        class="cal-agenda__banner"
        :style="{ '--month-index': month.day.month() }"
      >
        <q-icon name="mdi-movie-open-outline" class="cal-agenda__banner-icon" />
        <span class="cal-agenda__banner-number">{{ String(month.day.month() + 1).padStart(2, '0') }}</span>
        <div class="cal-agenda__banner-text">
          <span class="cal-agenda__banner-month">{{ formatters.monthLong.format(month.day.toDate()) }}</span>
          <span class="cal-agenda__banner-year">{{ month.day.year() }}</span>
        </div>
      </header>
      <div
        v-for="week in month.weeks"
        :key="week.key"
        class="cal-agenda__week"
      >
        <div class="cal-agenda__week-label">
          {{ week.label }}
        </div>
        <div
          v-for="entry in week.days"
          :key="entry.key"
          :data-day="entry.key"
          class="cal-agenda__day"
          :class="{ 'cal-agenda__day--today': entry.today, 'cal-agenda__day--past': entry.past }"
        >
          <div class="cal-agenda__date">
            <span class="cal-agenda__weekday">{{ formatters.weekday.format(entry.day.toDate()) }}</span>
            <span class="cal-agenda__number">{{ entry.day.date() }}</span>
          </div>
          <div class="cal-agenda__cards">
            <div v-if="!entry.cards.length" class="cal-agenda__empty">
              {{ t('common.nothingPlanned') }}
            </div>
            <transition-group name="cal-event-fade">
              <div
                v-for="(card, index) in entry.cards"
                :key="card.key"
                class="cal-agenda__item"
              >
                <div v-if="index === entry.nowIndex" class="cal-agenda__now" />
                <div
                  class="cal-event cal-event--card"
                  :data-event-id="card.event.id"
                  :class="eventClasses(card.classTarget)"
                  :style="{ '--event-color': card.event.color }"
                  @click="emit('event-click', card.event)"
                >
                  <div class="cal-event__card-head">
                    <q-icon
                      v-if="card.event.type === eventTypeEnum.ABSENCE"
                      :name="ABSENCE_ICON"
                      size="1rem"
                    />
                    <span class="cal-event__title">{{ card.title }}</span>
                    <span v-if="card.span" class="cal-event__span">
                      {{ t('common.dayOfSpan', { day: card.span.day, days: card.span.days }) }}
                    </span>
                  </div>
                  <span class="cal-event__time">{{ card.time }}</span>
                  <div v-if="card.resources.length" class="cal-event__resources">
                    <span
                      v-for="resource in card.resources"
                      :key="resource.id"
                      class="cal-event__resource"
                    >
                      <span class="cal-event__resource-dot" :style="{ backgroundColor: resource.color }" />
                      {{ resource.label }}
                    </span>
                    <span v-if="card.moreResources" class="cal-event__resource">+{{ card.moreResources }}</span>
                  </div>
                </div>
              </div>
            </transition-group>
            <div v-if="entry.cards.length && entry.nowIndex === entry.cards.length" class="cal-agenda__now" />
          </div>
        </div>
      </div>
    </section>
    <div ref="sentinelRef" class="cal-agenda__sentinel">
      <q-spinner-dots v-if="loading" color="primary" size="md" />
    </div>
  </div>
</template>
<style scoped lang="scss">
.cal-agenda {
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.5rem 0.75rem 1rem;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--q-primary) 30%, transparent) transparent;
}

.cal-agenda__month + .cal-agenda__month {
  margin-top: 1rem;
}

// --- Month banner: a film strip tinted by the month ---
.cal-agenda__banner {
  position: relative;
  display: flex;
  align-items: flex-end;
  height: 5.5rem;
  margin-bottom: 0.5rem;
  padding: 1rem 1.25rem;
  overflow: hidden;
  border: 1px solid var(--cal-border);
  border-radius: 1rem;
  color: color-mix(in srgb, var(--q-primary) 80%, var(--cal-text));
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -2;
    background:
      radial-gradient(120% 140% at 100% 0%, color-mix(in srgb, var(--q-secondary) 14%, transparent), transparent 60%),
      linear-gradient(135deg, color-mix(in srgb, var(--q-primary) 12%, var(--cal-surface)), color-mix(in srgb, var(--q-primary) 3%, var(--cal-surface)));
    filter: hue-rotate(calc(var(--month-index) * 30deg));
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background:
      repeating-linear-gradient(90deg, color-mix(in srgb, var(--q-primary) 14%, transparent) 0 0.625rem, transparent 0.625rem 1.25rem) top 0.375rem left 0.5rem / 100% 0.375rem no-repeat,
      repeating-linear-gradient(90deg, color-mix(in srgb, var(--q-primary) 14%, transparent) 0 0.625rem, transparent 0.625rem 1.25rem) bottom 0.375rem left 0.5rem / 100% 0.375rem no-repeat;
  }
}

.cal-agenda__banner-icon {
  position: absolute;
  right: 1rem;
  bottom: -0.75rem;
  z-index: -1;
  font-size: 5.5rem;
  opacity: 0.08;
  transform: rotate(-12deg);
}

.cal-agenda__banner-number {
  position: absolute;
  top: 0.625rem;
  right: 1.25rem;
  font-size: 2.75rem;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  opacity: 0.12;
}

.cal-agenda__banner-text {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.cal-agenda__banner-month {
  font-size: 1.375rem;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: -0.01em;
}

.cal-agenda__banner-year {
  font-size: 1rem;
  font-weight: 500;
  opacity: 0.7;
}

// --- Weeks & days ---
.cal-agenda__week-label {
  padding: 0.75rem 0 0.25rem 4rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--cal-text-muted);
}

.cal-agenda__day {
  display: flex;
  gap: 0.75rem;
  padding: 0.375rem 0;
}

.cal-agenda__date {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 3.25rem;
  padding-top: 0.125rem;
}

.cal-agenda__weekday {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--cal-text-muted);
}

.cal-agenda__number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 50%;
  font-size: 1rem;
  font-weight: 500;
}

.cal-agenda__day--past .cal-agenda__number {
  color: var(--cal-text-muted);
}

.cal-agenda__day--today {
  margin: 0 -0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.75rem;
  background: var(--cal-today);

  .cal-agenda__weekday {
    color: var(--q-primary);
  }

  .cal-agenda__number {
    background: var(--q-primary);
    color: white;
    box-shadow: 0 0.375rem 0.875rem -0.375rem var(--q-primary);
  }
}

.cal-agenda__cards {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.cal-agenda__item {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.cal-agenda__item.cal-event-fade-leave-active {
  position: absolute;
  left: 0;
  right: 0;
}

.cal-agenda__empty {
  display: flex;
  align-items: center;
  min-height: 2.5rem;
  font-size: 0.8125rem;
  color: var(--cal-text-muted);
}

.cal-agenda__now {
  position: relative;
  height: 0;
  margin: 0.125rem 0;
  border-top: 2px solid var(--cal-now);

  &::before {
    content: '';
    position: absolute;
    top: -0.40625rem;
    left: -0.375rem;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background: var(--cal-now);
  }
}

.cal-agenda__sentinel {
  display: flex;
  justify-content: center;
  min-height: 3rem;
  padding: 0.75rem 0;
}
</style>
